import React, { useState, useEffect } from 'react';
import { useModalEscape } from '../utils/useModalEscape';

const STORAGE_KEYS = {
  method: 'printDefaultMethod', // 'ticket' | 'a4' | 'pdf' | 'none'
  auto: 'printAutoMode',
  leftName: 'ticketLeftName',
  logo: 'ticketLogoData',
};

const modalBackdrop = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const modalCard = {
  background: '#fff',
  padding: 20,
  borderRadius: 10,
  width: 620,
  maxWidth: '96%',
  boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
  fontFamily: 'Arial, sans-serif'
};

const labelStyle = { display: 'block', marginBottom: 6, fontWeight: 600 };
const inputBase = { width: '100%', padding: '10px 12px', borderRadius: 6, border: '1px solid #dcdcdc' };
const smallMuted = { fontSize: 13, color: '#666' };

const btnPrimary = { background: '#007bff', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer' };
const btnSecondary = { background: '#f0f0f0', color: '#333', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' };
const btnGhost = { background: 'transparent', color: '#666', border: '1px solid #eee', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' };

const PrintSettings = ({ open, onClose }) => {
  const [method, setMethod] = useState(localStorage.getItem(STORAGE_KEYS.method) || 'ticket');
  const [auto, setAuto] = useState(localStorage.getItem(STORAGE_KEYS.auto) === 'true');
  const [leftName, setLeftName] = useState(localStorage.getItem(STORAGE_KEYS.leftName) || '');
  const [logoData, setLogoData] = useState(localStorage.getItem(STORAGE_KEYS.logo) || '');

  // ESC key handler for modal
  useModalEscape(open, onClose);

  useEffect(() => {
    setMethod(localStorage.getItem(STORAGE_KEYS.method) || 'ticket');
    setAuto(localStorage.getItem(STORAGE_KEYS.auto) === 'true');
    setLeftName(localStorage.getItem(STORAGE_KEYS.leftName) || '');
    setLogoData(localStorage.getItem(STORAGE_KEYS.logo) || '');
  }, [open]);

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEYS.method, method);
    localStorage.setItem(STORAGE_KEYS.auto, auto ? 'true' : 'false');
    localStorage.setItem(STORAGE_KEYS.leftName, leftName || '');
    if (logoData) localStorage.setItem(STORAGE_KEYS.logo, logoData);
    else localStorage.removeItem(STORAGE_KEYS.logo);
    onClose && onClose();
  };

  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEYS.method);
    localStorage.removeItem(STORAGE_KEYS.auto);
    localStorage.removeItem(STORAGE_KEYS.leftName);
    localStorage.removeItem(STORAGE_KEYS.logo);
    setMethod('ticket');
    setAuto(false);
    setLeftName('');
    setLogoData('');
  };

  const handleLogoChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setLogoData(reader.result);
    };
    reader.readAsDataURL(f);
  };

  if (!open) return null;

  return (
    <div style={modalBackdrop}>
      <div style={modalCard} role="dialog" aria-modal="true">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ margin: 0 }}>Configuración de impresión</h2>
          <button aria-label="Cerrar" onClick={onClose} style={{ ...btnGhost, padding: '6px 10px' }}>✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 18, alignItems: 'start' }}>
          <div>
            <label style={labelStyle}>Método predeterminado</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)} style={inputBase}>
              <option value="ticket">Ticket 80mm</option>
              <option value="a4">A4</option>
              <option value="pdf">PDF (abrir)</option>
              <option value="none">No imprimir</option>
            </select>

            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />
                <span style={smallMuted}>Enviar con método predeterminado automáticamente al presionar Ticket/Factura (sin vista previa)</span>
              </label>
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={labelStyle}>Nombre / texto en la izquierda</label>
              <input type="text" value={leftName} onChange={(e) => setLeftName(e.target.value)} style={inputBase} placeholder="Ej: NOMBRE COMERCIAL" />
              <div style={{ marginTop: 6 }}><span style={smallMuted}>Aparece a la izquierda del PV/Factura en los encabezados.</span></div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={labelStyle}>Logo (opcional)</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="file" accept="image/*" onChange={handleLogoChange} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: '#666' }}>Se mostrará en la izquierda del encabezado.</div>
              </div>
            </div>
            {logoData ? (
              <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                <img src={logoData} alt="logo preview" style={{ maxWidth: 180, maxHeight: 80, borderRadius: 6, border: '1px solid #eee' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <button onClick={() => setLogoData('')} style={btnSecondary}>Quitar logo</button>
                  <button onClick={() => { /* re-open file dialog */ }} style={btnGhost}>Reemplazar</button>
                </div>
              </div>
            ) : (
              <div style={{ marginTop: 8 }}>
                <div style={smallMuted}>Sin logo configurado.</div>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={handleReset} style={btnGhost}>Restaurar por defecto</button>
          <button onClick={onClose} style={btnSecondary}>Cancelar</button>
          <button onClick={handleSave} style={btnPrimary}>Guardar</button>
        </div>
      </div>
    </div>
  );
};

export default PrintSettings;
